import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import toast, {Toaster} from 'react-hot-toast'
import {useAuth} from '../../../../modules/auth/core/Auth'
import DashboardPhoneModel from '../../../newdashboard/DashboardPhoneModel'

type Props = {
  show: boolean
  handleClose: () => void
}

const DashboardPhoneModal: React.FC<Props> = ({show, handleClose}) => {
  const {currentUser} = useAuth()
  const [newPhone, setNewPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const isPhoneValid = /^\d{10}$/.test(newPhone)
  const isOtpValid = /^\d{6}$/.test(otp)

  useEffect(() => {
    if (show) {
      setNewPhone((currentUser?.PhoneNumber || '').replace(/\D/g, '').slice(0, 10))
      setOtp('')
      setOtpSent(false)
      setLoading(false)
    }
  }, [show, currentUser?.PhoneNumber])

  const closeModal = () => {
    if (loading) return

    setOtp('')
    setOtpSent(false)
    handleClose()
  }

  const sendOtp = async () => {
    try {
      setLoading(true)
      await DashboardPhoneModel.sendOtpForPhoneUpdate(
        newPhone,
        {
          CRMClientId: currentUser?.CRMClientId,
          EmployeeId: currentUser?.EmployeeId,
        },
        currentUser?.sessioncode
      )
      toast.success('OTP sent in Email')
      setOtpSent(true)
    } catch (err: any) {
      console.error(err)
      toast.error(err?.message || 'Failed to send OTP')
    } finally {
      setLoading(false)
    }
  }

  const verifyOtp = async () => {
    try {
      setLoading(true)
      const res = await DashboardPhoneModel.submitOtpForPhoneUpdate(
        newPhone,
        otp,
        {
          CRMClientId: currentUser?.CRMClientId,
          EmployeeId: currentUser?.EmployeeId,
        },
        currentUser?.sessioncode
      )

      if (res && res.status === 'otpwrong') {
        toast.error('Invalid OTP — please re-enter')
        setOtp('')
      } else {
        toast.success('Phone Number updated')
        window.location.reload()
      }
    } catch (err: any) {
      console.error(err)
      toast.error(err?.message || 'Failed to verify OTP')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      className='modal fade'
      id='kt_modal_update_phone'
      data-backdrop='static'
      data-keyboard='false'
      tabIndex={-1}
      role='dialog'
      show={show}
      dialogClassName='modal-dialog-scrollable'
      aria-hidden={!show}
      backdrop='static'
      keyboard={false}
      onHide={closeModal}
    >
      <Toaster />
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Update Phone Number</h5>
          <button
            type='button'
            className='btn-close'
            aria-label='Close'
            disabled={loading}
            onClick={closeModal}
          />
        </div>

        <div className='modal-body'>
          <div className='mb-7'>
            <label className='form-label fw-bolder text-dark fs-6'>New Phone Number</label>
            <input
              type='tel'
              inputMode='numeric'
              autoComplete='tel'
              className='form-control form-control-solid'
              value={newPhone}
              disabled={loading || otpSent}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, '')
                setNewPhone(digits.slice(0, 10))
              }}
              placeholder='Enter new phone number (10 digits)'
            />
            {newPhone && !isPhoneValid && (
              <div className='text-danger small mt-1'>Phone must be exactly 10 digits.</div>
            )}
          </div>

          {!otpSent && (
            <div className='d-flex justify-content-end gap-2'>
              <button
                type='button'
                className='btn btn-light'
                disabled={loading}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary'
                disabled={loading || !isPhoneValid}
                onClick={sendOtp}
              >
                {!loading && 'Send OTP'}
                {loading && (
                  <span>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                )}
              </button>
            </div>
          )}

          {otpSent && (
            <>
              <div className='mb-7'>
                <label className='form-label fw-bolder text-dark fs-6'>Enter OTP</label>
                <input
                  type='text'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  className='form-control form-control-solid'
                  value={otp}
                  disabled={loading}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '')
                    setOtp(digits.slice(0, 6))
                  }}
                  placeholder='6-digit Email OTP'
                />
                {otp && !isOtpValid && (
                  <div className='text-danger small mt-1'>OTP must be exactly 6 digits.</div>
                )}
              </div>

              <div className='d-flex justify-content-end gap-2'>
                <button
                  type='button'
                  className='btn btn-light'
                  disabled={loading}
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  disabled={loading || !isOtpValid}
                  onClick={verifyOtp}
                >
                  {!loading && 'Verify & Update'}
                  {loading && (
                    <span>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export {DashboardPhoneModal}
